import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import ErrorBoundary from './components/common/ErrorBoundary';
import { SignupProvider } from './components/auth/signup/SignupContext';
import PublicRoute from './components/auth/PublicRoute';
import { Navbar } from './components/layout/navmenu.jsx';
import Footer from './components/layout/Footer';
import Home from './components/home/Home';
import LoginModal from './components/auth/LoginModal';
import { LoadScript } from '@react-google-maps/api';
import VerifyAccount from './components/auth/signup/VerifyAccount';
import SignupEmailCollect from './components/auth/signup/SignupEmailCollect';
import Profile from './components/profile/userprofile';
import ClosingPage from './components/auth/ClosingPage';
import ForgotPasswordModal from './components/auth/ForgotPasswordModal';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CompanyProfile from './components/profile/companyprofile';
import { useSignup } from './components/auth/signup/SignupContext';
import PublicCompanyProfile from './components/profile/publiccompanyprofile';
import CustomerInfo from './components/profile/customerinfo';
import Terms from './components/legal/terms';
import Privacy from './components/legal/privacy';
import Cookies from './components/legal/cookies';
import NewUserProfile from './components/profile/NewUserProfile';
import TestPage from './pages/testpage';
import CreatePasswordFlow from './components/auth/signup/CreatePasswordFlow';
import CustomerSurveyForm from './components/profile/customersurveyform';
import TeamInfo from './components/profile/teaminfo';
import { useProfileStore } from './state/profileStore';
import { useMessagingStore } from './state/messagingStore';
import MessageCenter from './components/messaging/MessageCenter';
import { useModalStore } from './state/modalStore';
import TeamModal from './components/teams/TeamModal';
import CandidateListModal from './components/teams/candidatelistmodal';
import RecruitingModal from './components/teams/RecruitingModal';
import DesignCenter from './design/designCenter';
import SharingCenter from './share/sharingCenter';
import HelpCenter from './components/helpcenter/HelpCenter';
import AdminDesignCenterReview from './components/admin/AdminDesignCenterReview';
import PublicLandingPage from './design/PublicLandingPage';
import PricingPage from './components/pricing/PricingPage';

// MUST be a module-level const, NOT an inline array literal in the LoadScript
// JSX below. LoadScript uses referential equality on the libraries prop to
// decide whether to reload the Google Maps API script. An inline
// `libraries={['places']}` creates a fresh array on every App.js render —
// LoadScript sees "different libraries" and re-downloads the entire Maps API
// (~200KB) + unmounts/remounts every Maps consumer (PlacesAutocomplete in the
// profile editor, GoogleMap on the Teams page, candidate-list address picker,
// etc). Brett caught this 2026-05-26 via the "Performance warning! LoadScript
// has been reloaded unintentionally!" console message the lib emits when it
// detects the prop churn. The cascading remounts were a major contributor to
// the 429 rate-limit storm and the sluggish/blank navigation between pages.
// MUST live below all imports — CRA's prod ESLint enforces import/first.
const GOOGLE_MAPS_LIBRARIES = ['places'];

// Old SPA routes /u/:slug and /company/public/:slug had a hardcoded
// canonical = homepage in public/index.html, so Google indexed all 189
// of them as duplicates. The public-site Next.js app at
// /userprofiles/users/:slug and /userprofiles/companies/:slug renders
// the same content with a proper per-page canonical, so we redirect
// any in-app navigation that still uses the old paths.
function RedirectToUserProfile() {
  const { slug } = useParams();
  return <Navigate to={`/userprofiles/users/${slug}`} replace />;
}
function RedirectToCompanyProfile() {
  const { slug } = useParams();
  return <Navigate to={`/userprofiles/companies/${slug}`} replace />;
}

function AppContent() {
  const profile = useProfileStore((s) => s.profile);
  const location = useLocation();
  // Diagnostic 2026-05-26: log every route resolution + when AppContent renders.
  // Pair with [nav] click logs in navmenu.jsx to measure click-to-route latency.
  // If [route] resolved fires immediately after [nav] click, navigation is fast.
  // If there's a big gap (>500ms), React is busy with other work — and the
  // [render] line counts will tell us how many times AppContent re-rendered
  // during the gap (high counts = something is forcing constant re-renders).
  console.log('[render] AppContent @', Date.now(), 'pathname=', location.pathname);
  useEffect(() => {
    console.log('[route] resolved →', location.pathname, '@', Date.now());
  }, [location.pathname]);
  const candidateOpen = useModalStore((s) => s.candidateOpen);
  const candidate = useModalStore((s) => s.candidate);
  const candidateViewerUId = useModalStore((s) => s.candidateViewerUId);
  const candidateTop = useModalStore((s) => s.candidateTop);
  const candidateRight = useModalStore((s) => s.candidateRight);
  const candidateOffsetRight = useModalStore((s) => s.candidateOffsetRight);
  const closeCandidate = useModalStore((s) => s.closeCandidate);
  const isPublicProfilePage =
    location.pathname.startsWith('/u/') || location.pathname.startsWith('/company/public/');
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const tokenFromURL = queryParams.get('token');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const { handleLoginClick: handleLoginFromSignup, handleSignupClick } = useSignup();

  useEffect(() => {
    if (location.pathname === '/reset-password' && tokenFromURL) {
      setResetToken(tokenFromURL);
      setIsForgotPasswordModalOpen(true);
    }
  }, [location.pathname, tokenFromURL]);

  // Deep link from daily digest email: ?openSharingCenter=1
  // If logged in, open the Sharing Center overlay. If not, prompt login
  // (the flag stays in the URL so it fires again after successful login).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openSharingCenter') !== '1') return;
    if (profile?.u_id) {
      useModalStore.getState().openSharingCenter();
      // Strip the query param so it doesn't re-fire on subsequent renders
      const url = new URL(window.location.href);
      url.searchParams.delete('openSharingCenter');
      window.history.replaceState({}, '', url.toString());
    } else {
      setIsLoginModalOpen(true);
    }
  }, [location.search, profile?.u_id]);

  useEffect(() => {
    const handleLoginFromSignupClick = () => {
      setIsLoginModalOpen(true);
    };

    window.addEventListener('loginFromSignup', handleLoginFromSignupClick);
    return () => window.removeEventListener('loginFromSignup', handleLoginFromSignupClick);
  }, []);

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleCloseLogin = () => {
    setIsLoginModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar onLoginClick={handleLoginClick} onSignupClick={handleSignupClick} />
      <div className="flex-grow flex flex-col min-h-[calc(100vh-4rem)] pb-[0px]">
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={handleCloseLogin}
          onForgotPassword={() => setIsForgotPasswordModalOpen(true)}
          onSignupClick={handleSignupClick}
        />
        <ForgotPasswordModal
          isOpen={isForgotPasswordModalOpen}
          onClose={() => {
            setIsForgotPasswordModalOpen(false);
            setResetToken(null);
          }}
          onLoginClick={() => {
            setIsForgotPasswordModalOpen(false);
            setIsLoginModalOpen(true);
            setResetToken(null);
          }}
          resetToken={resetToken}
        />
  <LoadScript
    googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
    libraries={GOOGLE_MAPS_LIBRARIES}
    loadingElement={<div style={{ display: 'none' }} />}
  >

        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <Home />
              </PublicRoute>
            }
          />

          <Route path="/pricing" element={<PricingPage />} />


          <Route path="/verify-account" element={<VerifyAccount />} />
          <Route
            path="/test"
            element={
              <PublicRoute>
                <TestPage />
              </PublicRoute>
            }
          />
          <Route path="/create-password" element={<CreatePasswordFlow />} />
          <Route path="/signup/email" element={<SignupEmailCollect />} />

          <Route
            path="/profile/id/:id"
            element={
              <ProtectedRoute>
                <NewUserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:slug"
            element={
              <ProtectedRoute deferWhileLoading>
                <Profile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute deferWhileLoading>
                <Profile />
              </ProtectedRoute>
            }
          />

          <Route path="/u/:slug" element={<RedirectToUserProfile />} />

          {/* Public Landing Pages */}
          <Route path="/p/:slug" element={<PublicLandingPage />} />

          <Route path="/closing" element={<ClosingPage />} />

          {/* Single-segment /company/public/:slug redirects to public-site Next.js.
              Multi-segment franchise URLs (e.g. /company/public/servpro.com/locations/fl/...)
              still render the SPA component below. React Router picks the more
              specific :slug match for single-segment paths. */}
          <Route path="/company/public/:slug" element={<RedirectToCompanyProfile />} />
          <Route path="/company/public/*" element={<PublicCompanyProfile />} />
          {/* Internal "my own company" route — stable b_id-based lookup that
              doesn't depend on the URL-parseability of b_website. Used by
              navmenu + userprofile's "go to company" links since GBP / long-
              tail signups store platform URLs (with /, ?, etc.) in b_website
              that the splat route below can't carry cleanly. MUST be ordered
              before /company/* so the more-specific path wins. */}
          <Route
            path="/company/by-id/:id"
            element={
              <ProtectedRoute deferWhileLoading>
                <CompanyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/*"
            element={
              <ProtectedRoute deferWhileLoading>
                <CompanyProfile />
              </ProtectedRoute>
            }
          />

          
          <Route
            path="/customers"
            element={
              <ProtectedRoute deferWhileLoading>
                <CustomerInfo />
              </ProtectedRoute>
            }
          />

          <Route
            path="/teams"
            element={
              <ProtectedRoute deferWhileLoading>
                <TeamInfo />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/designcenter"
            element={
              <ProtectedRoute deferWhileLoading>
                {profile?.u_id === 109 && profile?.u_is_admin === true ? (
                  <AdminDesignCenterReview />
                ) : (
                  <div className="p-6 text-center text-gray-600">
                    Not authorized.
                  </div>
                )}
              </ProtectedRoute>
            }
          />


          {/* Legal */}
          <Route
            path="/terms"
            element={
              <PublicRoute>
                <Terms />
              </PublicRoute>
            }
          />
          <Route
            path="/privacy"
            element={
              <PublicRoute>
                <Privacy />
              </PublicRoute>
            }
          />
          <Route
            path="/review/:token"
            element={
              <PublicRoute>
                <CustomerSurveyForm />
              </PublicRoute>
            }
          />
          <Route
            path="/cookies"
            element={
              <PublicRoute>
                <Cookies />
              </PublicRoute>
            }
          />
        </Routes>

  </LoadScript>
       
      </div>
      <Footer />
      

      
      <MessageCenter />
      {/* CookieConsent moved to the public-facing search page —
          authenticated users are presumed to have accepted on signup. */}

      {/* Global modals (available on every page) */}
      <TeamModal />
      <RecruitingModal />
      <DesignCenter />
      <SharingCenter />
      <HelpCenter />

      <CandidateListModal
        opened={candidateOpen}
        onClose={closeCandidate}
        candidate={candidate}
        viewerUId={candidateViewerUId}
        top={candidateTop}
        right={candidateRight}
        offsetRight={candidateOffsetRight}
      />
    </div>

  );
}

function App() {
  return (
    <SignupProvider>
      <ErrorBoundary 
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="text-center max-w-md">
              <h1 className="text-2xl font-bold text-red-600 mb-4">System Error</h1>
              <p className="mb-6">We've encountered an unexpected issue. Our team has been notified.</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
              >
                Reload Application
              </button>
            </div>
          </div>
        }
      >
        <AppContent />

      </ErrorBoundary>
    </SignupProvider>
  );
}




export default App;
